import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaCalendarAlt, FaChartLine, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import './AdminPromotions.css';
import { API_BASE_URL } from '../utils/api';
import { ensureHttps } from '../utils/url';

// Cast icons to proper React element types to avoid TypeScript JSX component issues
const PlusIcon = FaPlus as React.ElementType;
const EditIcon = FaEdit as React.ElementType;
const TrashIcon = FaTrash as React.ElementType;
const EyeIcon = FaEye as React.ElementType;
const CalendarIcon = FaCalendarAlt as React.ElementType;
const ChartIcon = FaChartLine as React.ElementType;
const ToggleOnIcon = FaToggleOn as React.ElementType;
const ToggleOffIcon = FaToggleOff as React.ElementType;

interface Promotion {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  linkUrl?: string;
  type: 'promotion' | 'event' | 'advertisement' | 'announcement' | 'homepage_banner' | 'sidebar_ad' | 'category_promotion';
  displayLocation: 'neighborhoodpulse' | 'transferhub' | 'both' | 'homepage' | 'sidebar' | 'category';
  startDate?: string;
  endDate?: string;
  clicks: number;
  views: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PromotionForm {
  title: string;
  description: string;
  imageUrl: string;
  linkUrl: string;
  type: Promotion['type'];
  displayLocation: Promotion['displayLocation'];
  startDate: string;
  endDate: string;
  isActive: boolean;
}

const AdminPromotions: React.FC = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [formData, setFormData] = useState<PromotionForm>({
    title: '',
    description: '',
    imageUrl: '',
    linkUrl: '',
    type: 'promotion',
    displayLocation: 'neighborhoodpulse',
    startDate: '',
    endDate: '',
    isActive: true
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/promotions/admin`);
      if (response.ok) {
        const data = await response.json();
        setPromotions(data);
      } else {
        console.error('Failed to fetch promotions');
      }
    } catch (error) {
      console.error('Error fetching promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let finalImageUrl = formData.imageUrl;
      
      // If a file is selected, upload it first
      if (selectedFile) {
        const formDataFile = new FormData();
        formDataFile.append('image', selectedFile);
        
        const uploadResponse = await fetch(`${API_BASE_URL}/api/upload`, {
          method: 'POST',
          body: formDataFile,
        });
        
        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          finalImageUrl = uploadResult.imageUrl;
        } else {
          console.error('Failed to upload image');
          return;
        }
      }
      
      const url = editingPromotion 
        ? `${API_BASE_URL}/api/promotions/${editingPromotion.id}`
        : `${API_BASE_URL}/api/promotions`;
      
      const method = editingPromotion ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          imageUrl: finalImageUrl
        }),
      });

      if (response.ok) {
        await fetchPromotions();
        resetForm();
        setShowForm(false);
      } else {
        console.error('Failed to save promotion');
      }
    } catch (error) {
      console.error('Error saving promotion:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/promotions/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await fetchPromotions();
      } else {
        console.error('Failed to delete promotion');
      }
    } catch (error) {
      console.error('Error deleting promotion:', error);
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/promotions/${id}/toggle`, {
        method: 'PATCH'
      });

      if (response.ok) {
        await fetchPromotions();
      } else {
        console.error('Failed to update promotion status');
      }
    } catch (error) {
      console.error('Error updating promotion status:', error);
    }
  };

  const handleEdit = (promotion: Promotion) => {
    setEditingPromotion(promotion);
    setFormData({
      title: promotion.title,
      description: promotion.description,
      imageUrl: promotion.imageUrl || '',
      linkUrl: promotion.linkUrl || '',
      type: promotion.type,
      displayLocation: promotion.displayLocation,
      startDate: promotion.startDate ? promotion.startDate.split('T')[0] : '',
      endDate: promotion.endDate ? promotion.endDate.split('T')[0] : '',
      isActive: promotion.isActive
    });
    setSelectedFile(null);
    setImagePreview(promotion.imageUrl || '');
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      imageUrl: '',
      linkUrl: '',
      type: 'promotion',
      displayLocation: 'neighborhoodpulse',
      startDate: '',
      endDate: '',
      isActive: true
    });
    setEditingPromotion(null);
    setSelectedFile(null);
    setImagePreview('');
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'promotion': return '#ff6b6b';
      case 'event': return '#4ecdc4';
      case 'advertisement': return '#45b7d1';
      case 'announcement': return '#96ceb4';
      case 'homepage_banner': return '#f39c12';
      case 'sidebar_ad': return '#9b59b6';
      case 'category_promotion': return '#e67e22';
      default: return '#ff6b6b';
    }
  };

  const getStatusBadge = (promotion: Promotion) => {
    const now = new Date();
    const startDate = promotion.startDate ? new Date(promotion.startDate) : null;
    const endDate = promotion.endDate ? new Date(promotion.endDate) : null;
    
    if (!promotion.isActive) {
      return <span className="status-badge inactive">Inactive</span>;
    }
    
    if (startDate && now < startDate) {
      return <span className="status-badge scheduled">Scheduled</span>;
    }
    
    if (endDate && now > endDate) {
      return <span className="status-badge expired">Expired</span>;
    }
    
    return <span className="status-badge active">Active</span>;
  };

  if (loading) {
    return (
      <div className="admin-promotions">
        <div className="loading">Loading promotions...</div>
      </div>
    );
  }

  return (
    <div className="admin-promotions">
      <div className="admin-header">
        <h1>Promotion Management</h1>
        <button 
          className="add-btn"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <PlusIcon /> Add New Promotion
        </button>
      </div>

      {/* Statistics */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Promotions</h3>
          <p className="stat-number">{promotions.length}</p>
        </div>
        <div className="stat-card">
          <h3>Active Promotions</h3>
          <p className="stat-number">
            {promotions.filter(p => p.isActive).length}
          </p>
        </div>
        <div className="stat-card">
          <h3>Total Views</h3>
          <p className="stat-number">
            {promotions.reduce((sum, p) => sum + p.views, 0)}
          </p>
        </div>
        <div className="stat-card">
          <h3>Total Clicks</h3>
          <p className="stat-number">
            {promotions.reduce((sum, p) => sum + p.clicks, 0)}
          </p>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingPromotion ? 'Edit Promotion' : 'Add New Promotion'}</h2>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="promotion-form">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  placeholder="Enter promotion title"
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  placeholder="Enter promotion description"
                  rows={4}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value as Promotion['type']})}
                  >
                    <option value="promotion">Promotion</option>
                    <option value="event">Event</option>
                    <option value="advertisement">Advertisement</option>
                    <option value="announcement">Announcement</option>
                    <option value="homepage_banner">Homepage Banner Ad (R500/month)</option>
                    <option value="sidebar_ad">Sidebar Advertisement (R160/month)</option>
                    <option value="category_promotion">Category Page Promotion (R200/month)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Display Location</label>
                  <select
                    value={formData.displayLocation}
                    onChange={(e) => setFormData({...formData, displayLocation: e.target.value as Promotion['displayLocation']})}
                  >
                    <option value="neighborhoodpulse">NeighborhoodPulse Only</option>
                    <option value="transferhub">TransferHub Only</option>
                    <option value="both">Both Platforms</option>
                    <option value="homepage">Homepage (for Homepage Banner)</option>
                    <option value="sidebar">Sidebar (for Sidebar Ads)</option>
                    <option value="category">Category Pages (for Category Promotions)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                    />
                    <label htmlFor="isActive">
                      {formData.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
                      {formData.isActive ? 'Active' : 'Inactive'}
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Image</label>
                <div className="image-upload-section">
                  <div className="upload-options">
                    <div className="upload-option">
                      <label>Upload Image File:</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setSelectedFile(file);
                            setFormData({...formData, imageUrl: ''});
                            const reader = new FileReader();
                            reader.onload = (e) => {
                              setImagePreview(e.target?.result as string);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </div>
                    <div className="upload-option">
                      <label>Or Enter Image URL:</label>
                      <input
                        type="url"
                        value={formData.imageUrl}
                        onChange={(e) => {
                          setFormData({...formData, imageUrl: e.target.value});
                          setSelectedFile(null);
                          setImagePreview(e.target.value);
                        }}
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                  </div>
                  {imagePreview && (
                    <div className="image-preview">
                      <img src={ensureHttps(imagePreview)} alt="Preview" />
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Link URL</label>
                <input
                  type="url"
                  value={formData.linkUrl}
                  onChange={(e) => setFormData({...formData, linkUrl: e.target.value})}
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  {editingPromotion ? 'Update Promotion' : 'Create Promotion'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Promotions List */}
      <div className="promotions-grid">
        {promotions.map((promotion) => (
          <div key={promotion.id} className="promotion-card">
            <div className="promotion-header">
              <div className="promotion-type" style={{ backgroundColor: getTypeColor(promotion.type) + '20' }}>
                <span style={{ color: getTypeColor(promotion.type) }}>
                  {promotion.type.charAt(0).toUpperCase() + promotion.type.slice(1)}
                </span>
              </div>
              {getStatusBadge(promotion)}
            </div>

            <div className="promotion-content">
              {promotion.imageUrl && (
                <div className="promotion-image">
                  <img src={ensureHttps(promotion.imageUrl)} alt={promotion.title} />
                </div>
              )}
              
              <div className="promotion-details">
                <h3>{promotion.title}</h3>
                <p>{promotion.description}</p>
                
                <div className="promotion-meta">
                  <span>📍 Display: {promotion.displayLocation === 'neighborhoodpulse' ? 'NeighborhoodPulse' : 
                    promotion.displayLocation === 'transferhub' ? 'TransferHub' : 'Both Platforms'}</span>
                  {promotion.startDate && (
                    <span><CalendarIcon /> Start: {new Date(promotion.startDate).toLocaleDateString()}</span>
                  )}
                  {promotion.endDate && (
                    <span><CalendarIcon /> End: {new Date(promotion.endDate).toLocaleDateString()}</span>
                  )}
                </div>

                <div className="promotion-stats">
                  <span><EyeIcon /> {promotion.views} views</span>
                  <span><ChartIcon /> {promotion.clicks} clicks</span>
                </div>
              </div>
            </div>

            <div className="promotion-actions">
              <button 
                className="action-btn edit"
                onClick={() => handleEdit(promotion)}
              >
                <EditIcon /> Edit
              </button>
              <button 
                className="action-btn toggle"
                onClick={() => handleToggleStatus(promotion.id)}
              >
                {promotion.isActive ? <ToggleOffIcon /> : <ToggleOnIcon />}
                {promotion.isActive ? 'Deactivate' : 'Activate'}
              </button>
              <button 
                className="action-btn delete"
                onClick={() => handleDelete(promotion.id)}
              >
                <TrashIcon /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {promotions.length === 0 && (
        <div className="empty-state">
          <h3>No promotions yet</h3>
          <p>Create your first promotion to get started!</p>
          <button 
            className="add-btn"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            <PlusIcon /> Add New Promotion
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminPromotions; 